import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Monitor, Smartphone, Loader2, Plus, Check, Rocket } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function TemplatePreview() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add, has } = useCart();
  const [tpl, setTpl] = useState(null);
  const [html, setHtml] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [frameH, setFrameH] = useState(1000);
  const frameRef = useRef(null);

  const measure = () => {
    try {
      const doc = frameRef.current?.contentDocument;
      if (!doc?.body) return;
      const footer = doc.querySelector(".site-footer");
      const h = footer ? Math.ceil(footer.getBoundingClientRect().bottom + (doc.documentElement.scrollTop || doc.body.scrollTop || 0))
                        : Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      setFrameH(h);
    } catch { /* ignore */ }
  };
  useEffect(() => { const id = setTimeout(measure, 350); return () => clearTimeout(id); }, [device, html]);

  useEffect(() => {
    const onMsg = (e) => {
      if (!e.data) return;
      const frame = frameRef.current;
      if (!frame) return;
      if (e.data.__previewNav) {
        window.scrollTo({ top: Math.max(0, frame.getBoundingClientRect().top + window.pageYOffset - 90), behavior: "smooth" });
        setTimeout(measure, 150); setTimeout(measure, 550);
        return;
      }
      if (e.data.__previewScroll) {
        const y = frame.getBoundingClientRect().top + window.pageYOffset + (e.data.top || 0) - 90;
        window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const win = frameRef.current?.contentWindow;
        if (win) win.postMessage({ __scroll: window.scrollY || window.pageYOffset || 0 }, "*");
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    setTpl(null); setHtml(null);
    Promise.all([
      api.get(`/templates/${slug}`),
      api.get(`/templates/${slug}/preview`),
    ]).then(([d, p]) => {
      setTpl(d.data.template);
      setHtml(p.data.html);
      document.title = `Live Preview — ${d.data.template.name}`;
    }).catch(() => setHtml("<p style='padding:40px;font-family:sans-serif'>Preview unavailable.</p>"));
    return () => { document.title = "San Antonio's Web Design"; };
  }, [slug]);

  const inCart = tpl && has("template", tpl.slug);
  const cartItem = tpl && { kind: "template", id: tpl.slug, name: tpl.name, price: tpl.price, meta: `${tpl.type_label} · ${tpl.tier_label}` };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-testid="template-preview-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/templates" data-testid="preview-back" className="h-10 w-10 grid place-items-center rounded-full glass hover:text-primary shrink-0"><ArrowLeft className="h-5 w-5" /></Link>
          <div className="min-w-0">
            <h1 className="font-head font-extrabold text-xl leading-tight truncate">{tpl ? tpl.name : "Loading preview…"}</h1>
            {tpl && <p className="text-xs text-muted-foreground">{tpl.type_label} · {tpl.tier_label} · {tpl.business_type} · {tpl.pages} pages</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:inline-flex rounded-full glass p-1">
            <button data-testid="preview-desktop" onClick={() => setDevice("desktop")} className={`h-8 w-8 grid place-items-center rounded-full ${device === "desktop" ? "bg-primary text-primary-foreground" : ""}`}><Monitor className="h-4 w-4" /></button>
            <button data-testid="preview-mobile" onClick={() => setDevice("mobile")} className={`h-8 w-8 grid place-items-center rounded-full ${device === "mobile" ? "bg-primary text-primary-foreground" : ""}`}><Smartphone className="h-4 w-4" /></button>
          </div>
          {tpl && (
            <>
              <button data-testid="preview-add-cart" onClick={() => add(cartItem)}
                className={`inline-flex items-center gap-1 rounded-full px-4 h-10 text-sm font-semibold ${inCart ? "bg-emerald-500 text-white" : "border border-primary text-primary hover:bg-primary/10"}`}>
                {inCart ? <><Check className="h-4 w-4" /> In Cart</> : <><Plus className="h-4 w-4" /> Add ${tpl.price}</>}
              </button>
              <button data-testid="preview-buy-now" onClick={() => { add(cartItem); nav("/cart"); }}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 h-10 text-sm font-semibold hover:brightness-110 transition-[filter]">
                <Rocket className="h-4 w-4" /> Buy Now
              </button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-2xl glass p-2 sm:p-3">
        {html === null ? (
          <div className="h-[75vh] grid place-items-center"><Loader2 className="h-10 w-10 text-primary animate-spin" /></div>
        ) : (
          <div className="grid place-items-center">
            <div className={`bg-white rounded-xl overflow-hidden shadow-inner transition-all ${device === "mobile" ? "w-[390px] max-w-full" : "w-full"}`}>
              <iframe data-testid="preview-iframe" ref={frameRef} title={`${tpl?.name || "Template"} preview`} srcDoc={html}
                onLoad={measure} scrolling="no"
                className="w-full border-0 rounded-xl block" style={{ height: frameH }}
                sandbox="allow-scripts allow-same-origin" />
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">Like what you see? Get <b>{tpl?.name}</b> for <b>${tpl?.price}</b> — first-time customers get a free template.</p>
        <div className="flex gap-2">
          <Link to={`/templates/${slug}`} className="rounded-full glass px-5 h-10 grid place-items-center text-sm font-semibold hover:text-primary">View details</Link>
          {tpl && <button onClick={() => { add(cartItem); nav("/cart"); }} className="rounded-full bg-primary text-primary-foreground px-5 h-10 text-sm font-semibold">Buy Now</button>}
        </div>
      </div>
    </div>
  );
}
